//@flow
import React from 'react';
import routingActions from '../../Actions/routing';
import Optimizely from '../../Utils/optimizely-interface';
import {isQueryPreserved} from '../../ReactRouters/routeUtils';
import { browserHistory } from 'react-router';
import QuoteActions from '../../Actions/quote';
import WindowUtils from '../../Utils/window';
import FB from '../../Utils/fb-interface';
import Heap from '../../Utils/heap-interface';
import GoogleTagManager from '../../Utils/gtm-interface';
import UrlData from '../../Utils/url-data';
import ExperimentsActions from '../../Actions/experiments';
import PromotionsActions from '../../Actions/promotions';
import CookiesData from '../../Utils/cookies-data';
import QueryStringData from '../../Utils/query-string-data';
import MembershipActions from '../../Actions/membership';
import MktgListener from '../../../components/MktgListener';

import type { Location } from 'react-router';

type Props = {
  children:React$Node,
};

export default class PageWrapper extends React.PureComponent<Props> {
  unlisten:?()=>void;
  pathname:?string;

  constructor(props:Props) {
    super(props);

    QueryStringData.saveOriginalQueryString(window);

    const urlExperiments = UrlData.getExperiments();
    const cookieExperiments = CookiesData.getExperiments();
    const experiments = Object.assign({}, cookieExperiments, urlExperiments);
    if (Object.keys(experiments).length) {
      setTimeout(() => ExperimentsActions.enableExperiment(experiments), 0);
    }

    _addTimeSinceFirstSeenHeapEventProperty();
  }

  componentDidMount() {
    const nonMember = CookiesData.get('nonMemberPromo');
    if (nonMember) {
      setTimeout(() => PromotionsActions.setActivePromotion({ nonMember }), 0);
    }

    // activate optimizely on the first page render as well
    this._onPageLoad(UrlData.getPathname(), UrlData.getSearch());

    // listen for any page changes that don't cause a reload
    this.unlisten = browserHistory.listen(loc => this._onHistoryChange(loc));
  }

  _onHistoryChange(location:Location) {
    _routeFromCheckoutSuccess(location);

    if (location.action !== 'POP' && !location.hash) WindowUtils.scrollToTop();

    // due to a bug in react router v3, we get double callbacks
    if (!isQueryPreserved(location.search) || location.pathname === this.pathname) return;

    this.pathname = location.pathname;
    FB.AppEvents.logPageView();
    setTimeout(() => {
      /*
        This will trigger an action with information of the next route--use this
        to remove any temporary route-related data in the stores.
      */
      routingActions.changingRoute(location);
      this._onPageLoad(location.pathname, location.search);
    }, 0);
  }

  //actions we want to do on every hard page load and react router page change
  _onPageLoad(pathname:string, search:string) {
    Optimizely.activate();

    // Fire virtual page view event so that page views are
    // tracked correctly for single page app
    const fullPath = `${pathname}${search}`;
    const host = UrlData.getFullHostname();
    const url = `${host}${fullPath}`;
    GoogleTagManager.generalEvent({
      event: 'virtualPageView',
      pageUrl: url,
      pagePath: fullPath,
    });

    const params = UrlData.getParsedQuery(search);

    const promoCode = params && params.promo_code;
    if (promoCode) QuoteActions.setUrlPromoCode(promoCode);

    const planCode = UrlData.getMembershipPlan();
    if (planCode) MembershipActions.setNewMemberPlan(planCode);

    // For IRE / DMS integration, we need to pull this url
    // param when a user first loads app and set it in cookie
    // for later retrieval for tracking purposes
    // when user places an order
    const ireClickId = params && params.ire_clickid;
    if (ireClickId) CookiesData.set('ireClickId', ireClickId);
  }

  componentWillUnmount() {
    this.unlisten && this.unlisten();
  }

  render() {
    return (
      <React.Fragment>
        <MktgListener />
        {this.props.children}
      </React.Fragment>
    );
  }
}

//To guarantee that we refresh all data after an order is created, hard load the next page after checkout success
function _routeFromCheckoutSuccess(location:Location) {
  if (!window || !window.naturebox) return;
  const prevLocation = window.naturebox.previousLocation;
  const checkoutSuccess = /checkout\/success.*/;
  if (prevLocation && prevLocation.match(checkoutSuccess)) window.location = location.pathname;
  window.naturebox.previousLocation = location && location.pathname;
}

function _addTimeSinceFirstSeenHeapEventProperty() {
  let firstSeen = CookiesData.get('firstSeen');
  firstSeen = firstSeen && parseInt(firstSeen);

  // if the cookie is not there or if is not a number, reset it.
  if (!firstSeen) {
    firstSeen = Date.now();
    CookiesData.set('firstSeen', firstSeen, {expires: 3650});
  }

  setInterval(() => {
    Heap.addEventProperties({
      timeSinceFirstSeen: Date.now() - firstSeen,
    });
  }, 5000);
}
